import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  CheckCircle2, Upload, ShieldCheck, Camera, MapPin, Lock, ExternalLink,
  AlertCircle, Car, FileText, User as UserIcon,
} from "lucide-react";
import { api } from "@/lib/api";

// Seven-step gated onboarding — every step's `isComplete(state)` MUST return
// true before advancing, and the backend re-validates on the final
// PUT /cleaner/profile submission (verification_status flips to
// pending_id_review only when legal name + ID doc land together).
const STEPS = [
  { key: "identity",    label: "Identity",            desc: "Legal name, DOB, phone, photo" },
  { key: "zones",       label: "Service Area",        desc: "ZIPs + travel preference" },
  { key: "vehicle",     label: "Vehicle",             desc: "Transportation & vehicle info" },
  { key: "insurance",   label: "Auto Insurance",      desc: "Proof + policy state + acks" },
  { key: "background",  label: "Background & Safety", desc: "Screening acknowledgement" },
  { key: "id",          label: "ID Verification",     desc: "Government-issued photo ID" },
  { key: "payout",      label: "Payouts",             desc: "Connect Stripe for direct deposits" },
];

const VEHICLE_TYPES = [
  { v: "sedan",     label: "Sedan"     },
  { v: "coupe",     label: "Coupe"     },
  { v: "suv",       label: "SUV"       },
  { v: "crossover", label: "Crossover" },
  { v: "truck",     label: "Truck"     },
  { v: "van",       label: "Van"       },
  { v: "other",     label: "Other"     },
];

const TRANSPORT_TYPES = [
  { v: "own_vehicle",   label: "My own vehicle"     },
  { v: "public_transit",label: "Public transit"     },
  { v: "walk",          label: "Walk"               },
  { v: "bike",          label: "Bicycle"            },
  { v: "rideshare",     label: "Rideshare / taxi"   },
  { v: "other",         label: "Other"              },
];

export default function CleanerOnboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(null); // "id" | "photo" | "insurance" | null
  const [connecting, setConnecting] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [form, setForm] = useState({
    // Identity (Step 1)
    legal_first_name: "", legal_last_name: "",
    preferred_name: "", phone: "", date_of_birth: "", photo_url: "",
    // Mailing address (admin-only)
    address_line: "", address_unit: "", city: "", state: "", zip: "", country: "US",
    // Service area (Step 2) — separate from mailing address
    postal_codes: "",
    willing_to_travel: false, max_travel_miles: "",
    // Vehicle & Transportation (Step 3)
    transportation_type: "own_vehicle",
    vehicle_type: "", vehicle_year: "", vehicle_make: "", vehicle_model: "",
    vehicle_color: "", plate_state: "", license_plate: "", vin_last6: "",
    primary_vehicle: true,
    // Auto Insurance (Step 4)
    ins_carrier: "", ins_policy_number: "", ins_effective_date: "",
    ins_expiration_date: "", ins_document_url: "", ins_state: "",
    understands_business_use_ack: false,
    maintains_required_coverage_ack: false,
    attest_current_accurate: false,
    // Background & Safety (Step 5)
    bg_acknowledged: false,
    // ID doc (Step 6)
    id_document_url: "",
  });
  const [insuranceOnFile, setInsuranceOnFile] = useState([]);

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const loadProfile = async () => {
    try {
      const [pr, vh, ins] = await Promise.all([
        api.get("/cleaner/profile"),
        api.get("/cleaner/vehicle").catch(() => ({ data: {} })),
        api.get("/cleaner/insurance").catch(() => ({ data: [] })),
      ]);
      setProfile(pr.data);
      const p = pr.data || {};
      const v = vh.data || {};
      setForm(f => ({
        ...f,
        legal_first_name: p.legal_first_name || "",
        legal_last_name: p.legal_last_name || "",
        preferred_name: p.preferred_name || "",
        phone: p.phone || "",
        date_of_birth: p.date_of_birth || "",
        photo_url: p.photo_url || "",
        address_line: p.address_line || "",
        address_unit: p.address_unit || "",
        city: p.city || "",
        state: p.state || "",
        zip: p.zip || "",
        country: p.country || "US",
        postal_codes: (p.postal_codes || []).join(","),
        willing_to_travel: !!p.willing_to_travel,
        max_travel_miles: p.max_travel_miles != null ? String(p.max_travel_miles) : "",
        transportation_type: v.transportation_type || "own_vehicle",
        vehicle_type: v.vehicle_type || "",
        vehicle_year: v.year ? String(v.year) : "",
        vehicle_make: v.make || "",
        vehicle_model: v.model || "",
        vehicle_color: v.color || "",
        plate_state: v.plate_state || "",
        license_plate: v.license_plate || "",
        vin_last6: v.vin_last6 || "",
        primary_vehicle: v.primary_vehicle != null ? !!v.primary_vehicle : true,
        bg_acknowledged: !!(p.background_check_answers?.acknowledged),
        id_document_url: p.id_document_url || "",
      }));
      setInsuranceOnFile(ins.data || []);
    } catch (_) { /* silent — new signup */ }
    try {
      const s = await api.get("/connect/status");
      setStripeStatus(s.data);
    } catch (_) { /* silent */ }
  };
  useEffect(() => { loadProfile(); }, []);

  useEffect(() => {
    if (window.location.search.includes("payouts=return")) {
      loadProfile();
      toast.success("Welcome back — refreshing your Stripe status");
    }
  }, []);

  const uploadFile = async (file, target) => {
    if (!file) return;
    setUploading(target);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("bb_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}?auth=${token}`;
      if (target === "id") {
        update("id_document_url", url);
        try {
          await api.put("/cleaner/profile", {
            legal_first_name: form.legal_first_name,
            legal_last_name: form.legal_last_name,
            id_document_url: url,
          });
        } catch (_) { /* keep local state either way */ }
      } else if (target === "photo") {
        update("photo_url", url);
        try { await api.put("/cleaner/profile", { photo_url: url }); } catch (_) {}
      } else if (target === "insurance") {
        update("ins_document_url", data.url || data.path);
      }
      toast.success("File uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); }
  };

  const startStripeConnect = async () => {
    setConnecting(true);
    try {
      await api.post("/connect/account").catch(() => {});
      const { data } = await api.post("/connect/onboarding-link", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setConnecting(false);
      toast.error(e.response?.data?.detail || "Could not start Stripe onboarding");
    }
  };

  const isOwnVehicle = form.transportation_type === "own_vehicle";

  const saveStep = async () => {
    try {
      if (step === 0) {
        await api.put("/cleaner/profile", {
          legal_first_name: form.legal_first_name.trim(),
          legal_last_name: form.legal_last_name.trim(),
          preferred_name: form.preferred_name.trim(),
          phone: form.phone.trim(),
          date_of_birth: form.date_of_birth || null,
          photo_url: form.photo_url || "",
          address_line: form.address_line.trim(),
          address_unit: form.address_unit.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zip: form.zip.trim(),
          country: form.country.trim().toUpperCase(),
        });
      } else if (step === 1) {
        await api.put("/cleaner/profile", {
          postal_codes: form.postal_codes.split(",").map(s => s.trim()).filter(Boolean),
          willing_to_travel: !!form.willing_to_travel,
          max_travel_miles: form.max_travel_miles ? parseInt(form.max_travel_miles, 10) : null,
        });
      } else if (step === 2) {
        // Vehicle & Transportation
        const payload = {
          transportation_type: form.transportation_type,
          has_vehicle: isOwnVehicle,
        };
        if (isOwnVehicle) {
          Object.assign(payload, {
            vehicle_type: form.vehicle_type,
            year: form.vehicle_year ? parseInt(form.vehicle_year, 10) : null,
            make: form.vehicle_make.trim(),
            model: form.vehicle_model.trim(),
            color: form.vehicle_color.trim(),
            plate_state: form.plate_state.trim().toUpperCase(),
            license_plate: form.license_plate.trim(),
            vin_last6: form.vin_last6.trim(),
            primary_vehicle: !!form.primary_vehicle,
          });
        }
        await api.put("/cleaner/vehicle", payload);
      } else if (step === 3 && isOwnVehicle) {
        // Auto insurance — only submit if we have all required fields
        if (form.ins_carrier && form.ins_policy_number && form.ins_expiration_date && form.ins_document_url) {
          await api.post("/cleaner/insurance", {
            insurance_type: "auto",
            carrier: form.ins_carrier.trim(),
            policy_number: form.ins_policy_number.trim(),
            effective_date: form.ins_effective_date || "",
            expiration_date: form.ins_expiration_date,
            document_url: form.ins_document_url,
            insurance_state: form.ins_state.trim().toUpperCase(),
            attest_current_accurate: !!form.attest_current_accurate,
            understands_business_use_ack: !!form.understands_business_use_ack,
            maintains_required_coverage_ack: !!form.maintains_required_coverage_ack,
          });
          const ins = await api.get("/cleaner/insurance").catch(() => ({ data: [] }));
          setInsuranceOnFile(ins.data || []);
        }
      } else if (step === 4) {
        await api.put("/cleaner/profile", {
          background_check_answers: { acknowledged: form.bg_acknowledged, at: new Date().toISOString() },
        });
      }
    } catch (e) {
      // Non-blocking — user may correct and retry
      toast.error(e.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const isStepValid = (i) => {
    if (i === 0) {
      return !!(form.legal_first_name.trim() && form.legal_last_name.trim()
        && form.phone.trim() && form.date_of_birth
        && form.address_line.trim() && form.city.trim() && form.state.trim()
        && form.zip.trim() && (form.country || "").trim().length === 2);
    }
    if (i === 1) {
      const zones = form.postal_codes.split(",").map(s => s.trim()).filter(Boolean);
      return zones.length > 0;
    }
    if (i === 2) {
      if (!form.transportation_type) return false;
      if (!isOwnVehicle) return true; // non-drivers skip vehicle info
      return !!(form.vehicle_type && form.vehicle_year && form.vehicle_make
        && form.plate_state && form.license_plate);
    }
    if (i === 3) {
      if (!isOwnVehicle) return true; // insurance only mandatory for drivers
      // If a policy is already submitted (any status), the step is valid.
      if (insuranceOnFile.length > 0 && !form.ins_carrier) return true;
      return !!(form.ins_carrier && form.ins_policy_number && form.ins_expiration_date
        && form.ins_document_url && form.ins_state
        && form.attest_current_accurate
        && form.understands_business_use_ack
        && form.maintains_required_coverage_ack);
    }
    if (i === 4) return !!form.bg_acknowledged;
    if (i === 5) return !!form.id_document_url;
    if (i === 6) {
      const ss = stripeStatus?.stripe_status || {};
      return !!(ss.details_submitted || ss.payouts_enabled);
    }
    return false;
  };

  const next = async () => {
    if (!isStepValid(step)) {
      toast.error("Please complete every required field before continuing");
      return;
    }
    try { await saveStep(); }
    catch (_) { return; }
    setStep(s => Math.min(STEPS.length - 1, s + 1));
  };

  const submit = async () => {
    for (let i = 0; i < STEPS.length; i++) {
      if (!isStepValid(i)) {
        toast.error(`Cannot submit — Step ${i + 1} (${STEPS[i].label}) is incomplete`);
        setStep(i);
        return;
      }
    }
    try {
      await api.put("/cleaner/profile", {
        legal_first_name: form.legal_first_name.trim(),
        legal_last_name: form.legal_last_name.trim(),
        preferred_name: form.preferred_name.trim(),
        phone: form.phone.trim(),
        date_of_birth: form.date_of_birth || null,
        photo_url: form.photo_url || "",
        address_line: form.address_line.trim(),
        address_unit: form.address_unit.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        country: form.country.trim().toUpperCase(),
        postal_codes: form.postal_codes.split(",").map(s => s.trim()).filter(Boolean),
        willing_to_travel: !!form.willing_to_travel,
        max_travel_miles: form.max_travel_miles ? parseInt(form.max_travel_miles, 10) : null,
        background_check_answers: { acknowledged: true, at: new Date().toISOString() },
        id_document_url: form.id_document_url,
      });
      toast.success("Profile submitted — pending admin review");
      nav("/cleaner");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    }
  };

  const ss = stripeStatus?.stripe_status || {};
  const stripeReady = ss.payouts_enabled || ss.details_submitted;

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="cleaner-onboarding">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-teal-600">Contractor Onboarding</div>
        <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Become a CLEANBAZ Pro</h1>
        <p className="text-slate-600 mt-2">Complete every step in order. You cannot skip ahead.</p>
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2 text-xs font-mono text-slate-500 uppercase" data-testid="onb-stepper">
          {STEPS.map((s, i) => {
            const done = isStepValid(i);
            const active = i === step;
            return (
              <span key={s.key} className={`flex items-center gap-1 ${active ? "text-teal-600 font-semibold" : done ? "text-teal-500" : "text-slate-400"}`} data-testid={`onb-step-label-${i}`}>
                {done ? <CheckCircle2 className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {i + 1}. {s.label}
              </span>
            );
          })}
        </div>
        <Progress value={((step + 1) / STEPS.length) * 100} className="h-2" />
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-8 space-y-5">

          {/* Step 0 — Identity */}
          {step === 0 && (
            <div className="space-y-4" data-testid="onb-step-identity">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><UserIcon className="w-3 h-3" /> Personal information</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Legal First Name *</Label><Input value={form.legal_first_name} onChange={e => update("legal_first_name", e.target.value)} data-testid="onb-fname" className="mt-1.5" /></div>
                <div><Label>Legal Last Name *</Label><Input value={form.legal_last_name} onChange={e => update("legal_last_name", e.target.value)} data-testid="onb-lname" className="mt-1.5" /></div>
                <div><Label>Display Name</Label><Input value={form.preferred_name} onChange={e => update("preferred_name", e.target.value)} data-testid="onb-display-name" className="mt-1.5" placeholder="What customers see (optional)" /></div>
                <div><Label>Phone *</Label><Input value={form.phone} onChange={e => update("phone", e.target.value)} data-testid="onb-phone" className="mt-1.5" placeholder="+1 555 0100" /></div>
                <div><Label>Date of Birth *</Label><Input type="date" value={form.date_of_birth} onChange={e => update("date_of_birth", e.target.value)} data-testid="onb-dob" className="mt-1.5" /></div>
                <div>
                  <Label>Profile photo</Label>
                  <div className="mt-1.5 flex items-center gap-3">
                    {form.photo_url && <img src={form.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border border-slate-200" data-testid="onb-photo-preview" />}
                    <label className="inline-block">
                      <input type="file" accept="image/*" className="hidden" onChange={e => uploadFile(e.target.files?.[0], "photo")} data-testid="onb-photo-input" />
                      <Button asChild size="sm" variant="outline"><span data-testid="onb-photo-btn"><Camera className="w-3.5 h-3.5 mr-1" />{uploading === "photo" ? "Uploading…" : (form.photo_url ? "Replace" : "Upload")}</span></Button>
                    </label>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Mailing / residential address</div>
                <p className="text-xs text-slate-500 mb-3">Where CLEANBAZ can reach you. Never shown to customers. Separate from your service area ZIPs.</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="md:col-span-2"><Label>Street address *</Label><Input value={form.address_line} onChange={e => update("address_line", e.target.value)} data-testid="onb-street" className="mt-1.5" placeholder="123 Main St" /></div>
                  <div><Label>Apartment / Unit</Label><Input value={form.address_unit} onChange={e => update("address_unit", e.target.value)} data-testid="onb-unit" className="mt-1.5" placeholder="Apt 4B (optional)" /></div>
                  <div><Label>City *</Label><Input value={form.city} onChange={e => update("city", e.target.value)} data-testid="onb-city" className="mt-1.5" /></div>
                  <div><Label>State / Region *</Label><Input value={form.state} onChange={e => update("state", e.target.value)} data-testid="onb-state" className="mt-1.5" placeholder="FL" /></div>
                  <div><Label>ZIP / Postal Code *</Label><Input value={form.zip} onChange={e => update("zip", e.target.value)} data-testid="onb-zip" className="mt-1.5 font-mono" /></div>
                  <div><Label>Country *</Label><Input value={form.country} onChange={e => update("country", e.target.value.toUpperCase())} data-testid="onb-country" className="mt-1.5 font-mono" maxLength={2} /></div>
                </div>
              </div>
            </div>
          )}

          {/* Step 1 — Service zones + travel */}
          {step === 1 && (
            <div className="space-y-4" data-testid="onb-step-zones">
              <div>
                <Label>Postal codes you can service *</Label>
                <p className="text-xs text-slate-500 mt-1">Comma-separated. Your <b>normal</b> service area. Separate from your mailing address.</p>
                <Input placeholder="10001,10002,10003" value={form.postal_codes} onChange={e => update("postal_codes", e.target.value)} data-testid="onb-zones" className="mt-1.5 font-mono" />
              </div>
              <div className="border-t border-slate-200 pt-4">
                <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Travel preference</div>
                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="onb-travel-row">
                  <input type="checkbox" checked={form.willing_to_travel} onChange={e => update("willing_to_travel", e.target.checked)} data-testid="onb-willing-travel" className="mt-0.5 h-4 w-4 accent-teal-600" />
                  <span className="text-sm text-slate-800">
                    I'm willing to travel farther than my normal service area for eligible jobs.
                    <div className="text-xs text-slate-500 mt-1">Dispatch may still use proximity when matching, but you'll be able to see farther-away jobs.</div>
                  </span>
                </label>
                {form.willing_to_travel && (
                  <div className="mt-3 max-w-xs">
                    <Label>Max travel distance (miles, optional)</Label>
                    <Input type="number" min="0" max="500" value={form.max_travel_miles} onChange={e => update("max_travel_miles", e.target.value)} data-testid="onb-max-miles" className="mt-1.5" placeholder="25" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2 — Vehicle & Transportation */}
          {step === 2 && (
            <div className="space-y-4" data-testid="onb-step-vehicle">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1"><Car className="w-3 h-3" /> Transportation</div>
              <div>
                <Label>How do you get to jobs? *</Label>
                <div className="mt-2 grid sm:grid-cols-3 gap-2">
                  {TRANSPORT_TYPES.map(t => (
                    <button
                      key={t.v}
                      type="button"
                      data-testid={`onb-transport-${t.v}`}
                      onClick={() => update("transportation_type", t.v)}
                      className={`px-3 py-2 rounded-lg border text-sm transition-colors ${form.transportation_type === t.v ? "border-teal-500 bg-teal-50 text-teal-800" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}
                    >{t.label}</button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">If you don't drive to jobs, you'll only see jobs an admin has flagged as not requiring transportation.</p>
              </div>

              {isOwnVehicle && (
                <div className="border-t border-slate-200 pt-4 grid md:grid-cols-3 gap-3" data-testid="onb-vehicle-details">
                  <div className="md:col-span-3">
                    <Label>Vehicle type *</Label>
                    <select value={form.vehicle_type} onChange={e => update("vehicle_type", e.target.value)} data-testid="onb-vehicle-type" className="mt-1.5 w-full h-10 border rounded-md px-3 text-sm bg-white">
                      <option value="">Select…</option>
                      {VEHICLE_TYPES.map(v => <option key={v.v} value={v.v}>{v.label}</option>)}
                    </select>
                  </div>
                  <div><Label>Year *</Label><Input type="number" min="1950" max="2100" value={form.vehicle_year} onChange={e => update("vehicle_year", e.target.value)} data-testid="onb-vehicle-year" className="mt-1.5" placeholder="2020" /></div>
                  <div><Label>Make *</Label><Input value={form.vehicle_make} onChange={e => update("vehicle_make", e.target.value)} data-testid="onb-vehicle-make" className="mt-1.5" placeholder="Toyota" /></div>
                  <div><Label>Model</Label><Input value={form.vehicle_model} onChange={e => update("vehicle_model", e.target.value)} data-testid="onb-vehicle-model" className="mt-1.5" placeholder="Corolla" /></div>
                  <div><Label>Color</Label><Input value={form.vehicle_color} onChange={e => update("vehicle_color", e.target.value)} data-testid="onb-vehicle-color" className="mt-1.5" /></div>
                  <div><Label>Plate state *</Label><Input value={form.plate_state} onChange={e => update("plate_state", e.target.value.toUpperCase())} data-testid="onb-plate-state" className="mt-1.5 font-mono" placeholder="FL" maxLength={4} /></div>
                  <div><Label>License plate *</Label><Input value={form.license_plate} onChange={e => update("license_plate", e.target.value)} data-testid="onb-plate" className="mt-1.5 font-mono" placeholder="ABC1234" /></div>
                  <div className="md:col-span-2">
                    <Label>VIN (last 6, optional)</Label>
                    <Input value={form.vin_last6} onChange={e => update("vin_last6", e.target.value)} data-testid="onb-vin" className="mt-1.5 font-mono" placeholder="A1B2C3" maxLength={6} />
                    <p className="text-[11px] text-slate-500 mt-1">Admin-only. Never shown to customers or other cleaners. CLEANBAZ never stores the full VIN.</p>
                  </div>
                  <label className="md:col-span-3 flex items-start gap-3 p-3 rounded-lg border border-slate-200" data-testid="onb-primary-row">
                    <input type="checkbox" checked={form.primary_vehicle} onChange={e => update("primary_vehicle", e.target.checked)} data-testid="onb-primary-vehicle" className="mt-0.5 h-4 w-4 accent-teal-600" />
                    <span className="text-sm text-slate-800">This is the primary vehicle I use for CLEANBAZ work.</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Auto Insurance */}
          {step === 3 && (
            <div className="space-y-4" data-testid="onb-step-insurance">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Auto insurance</div>
              {!isOwnVehicle ? (
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700" data-testid="onb-insurance-skipped">
                  Auto insurance isn't required because your transportation type is <b>{TRANSPORT_TYPES.find(t => t.v === form.transportation_type)?.label || form.transportation_type}</b>. You can add it later from your Risk & Safety center if that changes.
                </div>
              ) : (
                <>
                  {insuranceOnFile.length > 0 && (
                    <div className="rounded-xl border border-teal-200 bg-teal-50 p-3 text-xs text-slate-700" data-testid="onb-insurance-on-file">
                      <div className="text-teal-700 font-semibold mb-1">Policy on file</div>
                      {insuranceOnFile.slice(0, 1).map(r => (
                        <div key={r.id}>{r.carrier} · ••••{r.policy_number_last4} · expires <b>{r.expiration_date}</b> · <span className="uppercase">{r.status?.replace("_", " ")}</span></div>
                      ))}
                      <div className="text-[11px] text-teal-800 mt-1">You can submit an updated policy below or continue to the next step.</div>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-3">
                    <div><Label>Insurance company *</Label><Input value={form.ins_carrier} onChange={e => update("ins_carrier", e.target.value)} data-testid="onb-ins-carrier" className="mt-1.5" placeholder="Geico, Progressive, …" /></div>
                    <div>
                      <Label>Policy number *</Label>
                      <Input value={form.ins_policy_number} onChange={e => update("ins_policy_number", e.target.value)} data-testid="onb-ins-policy" className="mt-1.5 font-mono" />
                      <p className="text-[11px] text-slate-500 mt-1">Admin-only. Only the last 4 digits are ever surfaced.</p>
                    </div>
                    <div><Label>Effective date</Label><Input type="date" value={form.ins_effective_date} onChange={e => update("ins_effective_date", e.target.value)} data-testid="onb-ins-effective" className="mt-1.5" /></div>
                    <div><Label>Expiration date *</Label><Input type="date" value={form.ins_expiration_date} onChange={e => update("ins_expiration_date", e.target.value)} data-testid="onb-ins-expiration" className="mt-1.5" /></div>
                    <div><Label>State of insurance *</Label><Input value={form.ins_state} onChange={e => update("ins_state", e.target.value.toUpperCase())} data-testid="onb-ins-state" className="mt-1.5 font-mono" placeholder="FL" maxLength={4} /></div>
                    <div>
                      <Label>Proof of insurance *</Label>
                      <label className="mt-1.5 flex items-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-teal-400">
                        <FileText className="w-4 h-4 text-slate-500" />
                        <span className="text-sm text-slate-700">{form.ins_document_url ? "Uploaded ✓ · Replace" : "Choose file"}</span>
                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => uploadFile(e.target.files?.[0], "insurance")} data-testid="onb-ins-file" />
                      </label>
                    </div>
                  </div>
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50" data-testid="onb-ins-business-ack-row">
                    <input type="checkbox" checked={form.understands_business_use_ack} onChange={e => update("understands_business_use_ack", e.target.checked)} data-testid="onb-ins-business-ack" className="mt-0.5 h-4 w-4 accent-teal-600" />
                    <span className="text-sm text-slate-800">I understand that a <b>personal</b> auto insurance policy may <b>not</b> cover business use, and CLEANBAZ is not my insurer.</span>
                  </label>
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50" data-testid="onb-ins-coverage-ack-row">
                    <input type="checkbox" checked={form.maintains_required_coverage_ack} onChange={e => update("maintains_required_coverage_ack", e.target.checked)} data-testid="onb-ins-coverage-ack" className="mt-0.5 h-4 w-4 accent-teal-600" />
                    <span className="text-sm text-slate-800">I confirm I maintain any legally and contractually required coverage for business-related driving.</span>
                  </label>
                  <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200" data-testid="onb-ins-attest-row">
                    <input type="checkbox" checked={form.attest_current_accurate} onChange={e => update("attest_current_accurate", e.target.checked)} data-testid="onb-ins-attest" className="mt-0.5 h-4 w-4 accent-teal-600" />
                    <span className="text-sm text-slate-800">The information and uploaded proof are <b>current and accurate</b>. Submitting false or expired coverage will block transportation-required jobs.</span>
                  </label>
                </>
              )}
            </div>
          )}

          {/* Step 4 — Background & Safety */}
          {step === 4 && (
            <div className="space-y-4" data-testid="onb-step-background">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div className="font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal-600" />Background &amp; Safety</div>
                <p className="text-sm text-slate-700 mt-2">
                  CLEANBAZ may conduct background and safety screening when required for certain services
                  or business purposes. Additional information or authorization may be requested separately
                  when applicable.
                </p>
              </div>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="onb-bg-ack-row">
                <input type="checkbox" checked={form.bg_acknowledged} onChange={e => update("bg_acknowledged", e.target.checked)} data-testid="onb-bg-ack" className="mt-0.5 h-4 w-4 accent-teal-600" />
                <span className="text-sm text-slate-800">I understand that CLEANBAZ may require background or safety screening before I can perform certain jobs.</span>
              </label>
            </div>
          )}

          {/* Step 5 — ID Verification */}
          {step === 5 && (
            <div className="space-y-4" data-testid="onb-step-id">
              <Label>Government photo ID *</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center bg-slate-50">
                {form.id_document_url ? (
                  <>
                    <img src={form.id_document_url} alt="ID" className="max-h-40 mx-auto rounded-lg" data-testid="onb-id-preview" />
                    <div className="text-sm text-teal-600 mt-3 flex items-center justify-center gap-1"><CheckCircle2 className="w-4 h-4" />Uploaded</div>
                    <label className="inline-block mt-3">
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => uploadFile(e.target.files?.[0], "id")} data-testid="onb-replace-input" />
                      <span className="text-xs text-slate-500 underline cursor-pointer">Replace file</span>
                    </label>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <div className="text-sm text-slate-600">Upload a photo of your passport or driver's license</div>
                    <label>
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => uploadFile(e.target.files?.[0], "id")} data-testid="onb-upload-input" />
                      <Button asChild className="mt-4 bg-teal-600 text-white" disabled={uploading === "id"}>
                        <span data-testid="onb-upload"><Camera className="w-4 h-4 mr-1" />{uploading === "id" ? "Uploading…" : "Choose file"}</span>
                      </Button>
                    </label>
                  </>
                )}
              </div>
              <div className="flex items-start gap-2 text-xs text-slate-500 bg-teal-50 p-3 rounded-lg">
                <ShieldCheck className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                <span>Your identity documents are encrypted at rest and reviewed by the CLEANBAZ compliance team within 24h. Never shown to customers.</span>
              </div>
            </div>
          )}

          {/* Step 6 — Stripe Connect (unchanged) */}
          {step === 6 && (
            <div className="space-y-4" data-testid="onb-step-payout">
              <div>
                <Label>Payout setup</Label>
                <p className="text-xs text-slate-500 mt-1">
                  CLEANBAZ uses <b>Stripe Connect</b> for direct deposits. CLEANBAZ does NOT store or see your bank credentials — Stripe hosts the entire onboarding form and holds your details securely.
                </p>
              </div>

              {stripeReady ? (
                <div className="p-4 rounded-xl bg-teal-50 border border-teal-200" data-testid="onb-stripe-connected">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold text-teal-900">Stripe connected</div>
                      <div className="text-sm text-teal-800 mt-1">
                        Payouts {ss.payouts_enabled ? <b>enabled</b> : <b>pending Stripe review</b>}
                        {ss.charges_enabled ? " · charges enabled" : ""}
                        {ss.external_last4 ? ` · bank ••••${ss.external_last4}` : ""}
                        {ss.external_bank_name ? ` (${ss.external_bank_name})` : ""}
                      </div>
                      {(ss.currently_due?.length > 0) && (
                        <div className="text-xs text-amber-800 mt-2">Stripe still needs: {ss.currently_due.join(", ")}</div>
                      )}
                    </div>
                    <Button variant="outline" onClick={startStripeConnect} disabled={connecting} data-testid="onb-stripe-manage">
                      <ExternalLink className="w-4 h-4 mr-1" />Manage
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-6 rounded-xl border-2 border-dashed border-slate-300 bg-white text-center">
                  <div className="text-sm text-slate-700 mb-4">Click below to connect your Stripe account and enable direct deposits.</div>
                  <Button onClick={startStripeConnect} disabled={connecting} data-testid="onb-stripe-connect" className="bg-teal-600 hover:bg-teal-700 text-white">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {connecting ? "Opening Stripe…" : "Connect Stripe Account"}
                  </Button>
                  <p className="text-[11px] text-slate-500 mt-3">You'll be redirected to Stripe's secure hosted onboarding.</p>
                </div>
              )}

              <div className="text-xs bg-slate-50 p-4 rounded-xl text-slate-600">
                Payout terms: You receive <b>80%</b> of every completed job, released directly by Stripe after service confirmation. CLEANBAZ retains a <b>20%</b> platform fee. No admin approval is required for withdrawals of your completed-job earnings.
              </div>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex justify-between pt-6 border-t border-slate-200">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep(s => s - 1)} data-testid="onb-back">Back</Button>
            {!isStepValid(step) && (
              <div className="flex items-center gap-1 text-xs text-amber-700" data-testid="onb-locked">
                <AlertCircle className="w-3.5 h-3.5" />Complete this step to continue
              </div>
            )}
            {step < STEPS.length - 1 ? (
              <Button onClick={next} data-testid="onb-next" disabled={!isStepValid(step)}
                      className="bg-teal-600 text-white disabled:bg-slate-300 disabled:cursor-not-allowed">
                Next
              </Button>
            ) : (
              <Button onClick={submit} data-testid="onb-submit" disabled={!isStepValid(step)}
                      className="bg-teal-600 text-white disabled:bg-slate-300 disabled:cursor-not-allowed">
                Submit for Review
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
